import React from "react";
import { FormikProps } from "formik";
import XGSFormInput from "../../../../ui-components/form/input/xgsFormInput";
import XGSFormSelect from "../../../../ui-components/form/select/xgsFormSelect";
import XGSFormTextarea from "../../../../ui-components/form/textarea/xgsFormTextarea";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import { countryOptions, statusOptions } from "app/data/terminal-details/models";
import usaStates from "../../../../app/data/common/usaStates";
import { Popover } from "ui-components/popover/popover";

interface CarrierFormFieldsProps {
    formikProps: FormikProps<any>;
    isEdit: boolean;
    CardComponent: React.ElementType<{ title: string; hideButtons: boolean; mix?: string; children: React.ReactNode; }>;
}

const CarrierFormFields = ({ formikProps, isEdit, CardComponent }: CarrierFormFieldsProps): React.ReactElement => {

    const getOption = (options: any[], value: string | undefined) => {
        if (!value) return null;

        return (
            options.find((option) =>
                option.value.toLowerCase() === value.toLowerCase() ||
                option.label.toLowerCase() === value.toLowerCase()
            ) || null
        );
    };

    const formatPhone = (value: string) => {
        const digits = value.replace(/\D/g, "");

        // format: (XXX) XXX-XXXX
        if (digits.length <= 3) return `(${digits}`;
        if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    };

    return (
        <div className="carrier-add-edit-section-container">
            <CardComponent title="" hideButtons={true} mix="carrier-info-card">
                <XGSFormInput
                    type="text"
                    name="carrierID"
                    labelMode={LabelModes.column}
                    label="Carrier ID (Auto-generated)"
                    placeholder="Carrier ID"
                    disabled
                />
                <XGSFormSelect
                    label="Status"
                    name="status"
                    required
                    placeholder="Select Status"
                    options={statusOptions}
                    value={getOption(statusOptions, formikProps.values.status)}
                    onValueChange={(option) => {
                        formikProps.setFieldValue("status", option?.value ?? "");
                    }}
                    formik={formikProps}
                    labelMode={LabelModes.column}
                />
                <XGSFormInput
                    type="text"
                    name="carrierName"
                    placeholder="Carrier Name"
                    labelMode={LabelModes.column}
                    label="Carrier Name"
                    required
                />

                <XGSFormInput
                    type="tel"
                    name="phone"
                    placeholder="(000) 000-0000"
                    labelMode={LabelModes.column}
                    label="Phone"
                    onChange={(e: any) => {
                        const formatted = formatPhone(e.target.value);
                        formikProps.setFieldValue("phone", formatted);
                    }}
                    required
                />
                <XGSFormInput
                    type="email"
                    name="email"
                    placeholder="abc@abc.com"
                    labelMode={LabelModes.column}
                    label="Email"
                    required
                />
                <div className="card__header">
                    <div className="card__header__content">
                        <div className="carrier-view-container card__title">Address</div>
                    </div>
                </div>
                <XGSFormInput
                    type="text"
                    name="streetAddress"
                    placeholder="Street Address"
                    labelMode={LabelModes.column}
                    label="Street Address"
                    required
                />
                <div className="grid-2col">
                    <XGSFormInput
                        type="text"
                        name="city"
                        placeholder="City"
                        labelMode={LabelModes.column}
                        label="City"
                        required
                    />
                    <XGSFormSelect
                        label="State/Province"
                        name="stateProvince"
                        required={true}
                        options={usaStates}
                        value={getOption(usaStates, formikProps.values.stateProvince)}
                        onValueChange={(option) => {
                            formikProps.setFieldValue("stateProvince", option?.value ?? "");
                        }}
                        formik={formikProps}
                        labelMode={LabelModes.column}
                    />
                </div>
                <div className="grid-2col">
                    <XGSFormInput
                        type="text"
                        name="zipPostalCode"
                        labelMode={LabelModes.column}
                        label="ZIP/Postal Code"
                        required
                    />
                    <XGSFormSelect
                        label="Country"
                        name="country"
                        required={true}
                        options={countryOptions}
                        value={getOption(countryOptions, formikProps.values.country)}
                        onValueChange={(option) => {
                            formikProps.setFieldValue("country", option?.value ?? "");
                        }}
                        formik={formikProps}
                        labelMode={LabelModes.column}
                    />
                </div>
            </CardComponent>

            <CardComponent title="Registration & ID" hideButtons={true} mix="registration-id-card">
                <XGSFormInput
                    type="text"
                    name="scacCode"
                    labelMode={LabelModes.column}
                    label={
                        <>
                            SCAC Code{" "}
                            <Popover
                                values={
                                    <div className="popover-value">
                                        SCAC (Standard Carrier Alpha Code) — a unique four-letter code assigned to carriers by NMFTA for identification in shipping and freight systems.
                                    </div>
                                }
                            />
                        </>
                    }
                    required
                />
                <XGSFormInput
                    type="text"
                    name="motorCarrierNo"
                    labelMode={LabelModes.column}
                    label={
                        <>
                            Motor Carrier (MC) No.
                            <Popover
                                values={
                                    <div className="popover-value">
                                        MC Number — a registration number issued by the FMCSA that allows carriers to operate as a for-hire transport company in the U.S.
                                    </div>
                                }
                            />
                        </>
                    }
                    required
                />
            </CardComponent>

            <CardComponent title="Insurance Expiration Dates" hideButtons={true} mix="insurance-expiration-dates">
                <div className="grid-2col">
                    <XGSFormInput
                        type="date"
                        name="generalLiabilityDate"
                        labelMode={LabelModes.column}
                        label={
                            <>
                                General Liability{" "}
                                <Popover
                                    values={
                                        <div className="popover-value">
                                            General Liability — covers third-party property or injury claims.
                                        </div>
                                    }
                                />
                            </>
                        }
                        required
                    />
                    <XGSFormInput
                        type="date"
                        name="autoLiabilityDate"
                        labelMode={LabelModes.column}
                        label={
                            <>
                                Auto Liability{" "}
                                <Popover
                                    values={
                                        <div className="popover-value">
                                            Auto Liability — covers damages caused by carrier vehicles.
                                        </div>
                                    }
                                />
                            </>
                        }
                        required
                    />
                </div>
                <div className="grid-2col">
                    <XGSFormInput
                        type="date"
                        name="motorCargoDate"
                        labelMode={LabelModes.column}
                        label={
                            <>
                                Motor Cargo{" "}
                                <Popover
                                    values={
                                        <div className="popover-value">
                                            Motor Cargo — protects goods being transported.
                                        </div>
                                    }
                                />
                            </>
                        }
                    />
                    <XGSFormInput
                        type="date"
                        name="workmanCompDate"
                        labelMode={LabelModes.column}
                        label={
                            <>
                                Workman's Comp{" "}
                                <Popover
                                    values={
                                        <div className="popover-value">
                                            Workman’s Comp — covers employee injury during work.
                                        </div>
                                    }
                                />
                            </>
                        }
                    />
                </div>
            </CardComponent>

            <CardComponent title="Notes" hideButtons={true}>
                <XGSFormTextarea
                    name="notes"
                    labelMode={LabelModes.column}
                    placeholder="Enter notes here..."
                />
            </CardComponent>
        </div>
    );
}

export default CarrierFormFields;